import {
  computed,
  reactive,
  useContext,
  useRoute,
} from "@nuxtjs/composition-api";
import { useVuelidate } from "@vuelidate/core";
import NEW_PERSON_MUTATION from "./createPersonAllInOne.graphql";
import { isCustomerLoginRequiredError } from "./registrationErrors";
import { useAuthStore } from "~/components/_refactored/auth";
import { useUserStore } from "~/stores/user";

const RegistrationType = {
  PROFESSIONAL: "professional",
  PRICE_OFFER_PROFESSIONAL: "price_offer_professional",
};

function initData() {
  return {
    email: "",
    password: "",
    professions: [],
    city: null,
    phone: {
      number: "",
      countryCode: null,
    },
    terms: false,
    dataTerms: false,
    name: "",
    campaignsCookie: "",
    registrationPromoCode: "",
    type: RegistrationType.PROFESSIONAL,
  };
}

const formData = reactive(initData());

const state = reactive({
  loading: false,
  selectedZoneProfessionCount: [],
  userId: null,
});

const maxNonZoneProfessionCount = 5;

function resetForm() {
  Object.assign(formData, initData());
}

export function useRegistration() {
  const {
    $validation: { rules },
    $cookie,
    $mutate,
    $sentry,
    $track,
    i18n,
  } = useContext();
  const authStore = useAuthStore();
  const userStore = useUserStore();

  const route = useRoute();

  function loadCampaignParams() {
    formData.campaignsCookie = $cookie.get("campaigns") ?? "";
    formData.registrationPromoCode = route.value.query.promoCode;
  }

  const maxLength = (value) => {
    if (formData.type === RegistrationType.PRICE_OFFER_PROFESSIONAL) {
      return value?.length === 0;
    }

    return (
      value?.length <=
      maxNonZoneProfessionCount + state.selectedZoneProfessionCount
    );
  };

  const nameValidator = (value) => {
    if (!value) return false;
    const spaceIndex = value.indexOf(" ");
    return value.length > 6 && spaceIndex > -1 && spaceIndex < value.length - 1;
  };

  const formRules = {
    name: {
      required: rules.required,
      nameValidator,
    },
    professions: {
      required: rules.requiredIf(
        () => formData.type === RegistrationType.PROFESSIONAL
      ),
      minLength:
        formData.type === RegistrationType.PROFESSIONAL
          ? rules.minLength(1)
          : rules.minLength(0),
      maxLength,
    },
    city: {
      required: rules.required,
    },
    password: {
      required: rules.required,
      minLength: rules.minLength(6),
    },
    email: {
      required: rules.required,
      email: rules.email,
    },
    terms: {
      consentRequired: rules.consentRequired(
        i18n.t("blogPage.acceptPrivacyPolicyErrorMessage")
      ),
    },
    dataTerms: {
      consentRequired: rules.consentRequired(
        i18n.t("blogPage.acceptPrivacyPolicyErrorMessage")
      ),
    },
    phone: {
      number: {
        required: rules.required,
      },
      countryCode: {
        required: rules.required,
      },
    },
    type: {
      required: rules.required,
    },
  };

  const v$ = useVuelidate(formRules, formData);

  const email = computed({
    get() {
      return formData.email;
    },
    set(value) {
      formData.email = value;
    },
  });

  const terms = computed({
    get() {
      return formData.terms;
    },
    set(value) {
      formData.terms = value;
    },
  });

  const dataTerms = computed({
    get() {
      return formData.dataTerms;
    },
    set(value) {
      formData.dataTerms = value;
    },
  });

  const password = computed({
    get() {
      return formData.password;
    },
    set(value) {
      formData.password = value;
    },
  });

  const city = computed({
    get() {
      return formData.city;
    },
    set(value) {
      formData.city = value;
    },
  });

  const professions = computed({
    get() {
      return formData.professions;
    },
    set(value) {
      formData.professions = value;
    },
  });

  const phone = computed({
    get() {
      return formData.phone;
    },
    set(value) {
      formData.phone = value;
    },
  });

  const name = computed({
    get() {
      return formData.name;
    },
    set(value) {
      formData.name = value;
    },
  });

  const type = computed({
    get() {
      return formData.type;
    },
    set(value) {
      formData.type = value;
    },
  });

  async function submitPerson() {
    let lastPerson = [];
    try {
      lastPerson = JSON.parse(localStorage.getItem("_lp") || "[]");
    } catch (error) {}

    const variables = {
      name: formData.name,
      email: formData.email,
      mobile: formData.phone.number,
      phoneCountryCodeId: formData.phone.countryCode.id,
      password: formData.password,
      professionIds: formData.professions.map((profession) =>
        Number(profession.id)
      ),
      cityId: Number(formData.city.id),
      lp: lastPerson,
      campaignsCookie: formData.campaignsCookie ?? "",
      registrationPromoCode: formData.promoCode ?? "",
      type: formData.type,
    };
    try {
      state.loading = true;

      const resp = await $mutate(NEW_PERSON_MUTATION, variables);

      if (!resp.success) {
        throw new Error(resp);
      }

      $track.analyticsEvent({
        category: "mate",
        action: "new-mate-registration",
      });

      state.userId = resp.userId;
      state.loading = false;

      try {
        await authStore.loginWithToken(resp.token);
        await userStore.updateLastPerson(resp.token);
      } catch (postErr) {
        // Registration succeeded server-side; don't surface a failure popup
        // for client-side post-login cleanup errors (e.g. localStorage writes
        // blocked in private mode).
        $sentry.captureException(postErr, {
          tags: { registration: "post-login" },
        });
      }

      return Promise.resolve();
    } catch (err) {
      $track.analyticsEvent({
        category: "error",
        action: "new-mate-registration",
      });
      $sentry.addBreadcrumb({
        category: "registration",
        message: JSON.stringify({ ...variables, password: undefined }),
        level: "info",
      });
      $sentry.captureException(err, {
        tags: {
          registration: "submit",
        },
      });
      throw err;
    } finally {
      state.loading = false;
    }
  }

  return {
    v$,
    submitPerson,
    isCustomerLoginRequiredError: (error) =>
      isCustomerLoginRequiredError(error, i18n),
    state,
    resetForm,
    email,
    terms,
    dataTerms,
    password,
    city,
    professions,
    phone,
    name,
    type,
    maxNonZoneProfessionCount,
    loadCampaignParams,
    RegistrationType,
  };
}
